/**
 * A colour per project, so a name you're scanning for has something to catch
 * the eye on before you've read it.
 *
 * **Derived by default, overridable.** A colour nobody had to choose is a
 * colour every project has from the moment it's imported — including the ones
 * created next week. `Project.color` overrides it when two projects that show
 * up side by side happen to land on the same hue.
 *
 * The override is a hex value (`#rrggbb`) picked in project settings; the eight
 * palette slot names are still accepted, since that's what older overrides hold.
 *
 * The palette itself lives in `styles/globals.css` as `--project-*` custom
 * properties with a light and a dark step each, so the theme swaps them without
 * this module knowing the theme exists.
 *
 * Eight hues, validated as a categorical set against Hub's own surfaces: every
 * adjacent pair clears the colour-vision-deficiency separation floor in both
 * modes. Three of the light steps sit under 3:1 contrast on white, which is
 * allowed here for one specific reason — **the project's name is always
 * rendered next to its colour**. The colour is a recognition aid layered on top
 * of a label, never the only thing carrying identity. That is also why more
 * than eight projects may share hues: with the name right there, a repeat is a
 * missed shortcut, not a misreading.
 */

export const PROJECT_COLORS = [
  "blue",
  "orange",
  "aqua",
  "yellow",
  "magenta",
  "green",
  "violet",
  "red",
] as const;

export type ProjectColor = (typeof PROJECT_COLORS)[number];

export function isProjectColor(value: string): value is ProjectColor {
  return (PROJECT_COLORS as readonly string[]).includes(value);
}

/** `#rrggbb`, the shape both the picker and the API accept. */
export const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export function isHexColor(value: string): boolean {
  return HEX_COLOR_RE.test(value);
}

/**
 * Stable hash of a project id onto a palette slot.
 *
 * Keyed on the id rather than the name so renaming a project doesn't recolour
 * it — the colour you learned to recognise stays put. FNV-1a: tiny, no
 * dependency, and spreads short cuid-like strings evenly enough that a handful
 * of projects rarely collide.
 */
function slotFor(projectId: string): ProjectColor {
  let hash = 0x811c9dc5;
  for (let i = 0; i < projectId.length; i++) {
    hash ^= projectId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return PROJECT_COLORS[Math.abs(hash) % PROJECT_COLORS.length] as ProjectColor;
}

/** The palette slot a project wears: its override, else the derived one. */
export function projectColorSlot(
  projectId: string,
  override?: string | null | undefined,
): ProjectColor {
  // An override from an older palette (or a hand-edited row) falls back to the
  // derived slot rather than rendering as nothing.
  if (override && isProjectColor(override)) return override;
  return slotFor(projectId);
}

/** A CSS colour for a project, ready for `style` or a Tailwind arbitrary value. */
export function projectColorVar(projectId: string, override?: string | null | undefined): string {
  // A hand-picked hex is used verbatim; anything else falls back to a palette
  // slot, which the theme swaps between its light and dark step.
  if (override && isHexColor(override)) return override.toLowerCase();
  return `var(--project-${projectColorSlot(projectId, override)})`;
}

/** Human label for the colour picker in project settings. */
export const PROJECT_COLOR_LABEL: Record<ProjectColor, string> = {
  blue: "Blue",
  orange: "Orange",
  aqua: "Aqua",
  yellow: "Yellow",
  magenta: "Magenta",
  green: "Green",
  violet: "Violet",
  red: "Red",
};
